import React, { useContext, useEffect, useRef, useState } from "react";
import { Switch } from "@mui/material";
import styles from "./styles/PartnerSharing.module.css";
import Info from "../Info/info";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import { SMOOTH_HEIGHT_MS } from "../../../Utils/useSmoothHeight";

/*
  Partner Sharing — the last section of the Add Asset and Edit Asset modals,
  below Credentials and separated from it by a divider.

  A single master switch decides whether this asset is shared with the user's
  partner at all; turning it on reveals four switches for what exactly is
  shared. Credentials are never shareable, which is what the note underneath
  says.

  Frontend only for now. The component owns nothing but its own shape: the
  parent holds the value and there is no endpoint behind it yet. The partner's
  name and picture are props with placeholders, because the app has no way to
  identify a user's partner today — see docs/partner-sharing-backend-spec.md.
*/

/*
  Each row's View flag and the Edit flag that answers to it.

  One pair per row, and the pairing is the rule: Edit cannot be on while View
  is off, because a field nobody may read is not a field anybody may change.
  The form keeps that true in both directions (see `update`), and the server
  checks it again rather than trusting the form -- see
  Backend/Utils/partnerEditable.js, which maps each field to this same pair.
*/
export const EDIT_FLAG_OF = {
  share_description: "edit_description",
  share_files: "edit_files",
  share_evaluations: "edit_evaluations",
  share_beneficiaries: "edit_beneficiaries",
  share_trusted_advisor: "edit_trusted_advisor",
};

// Every edit flag, off. The starting point for any asset.
export const EMPTY_EDIT_SHARING = Object.fromEntries(
  Object.values(EDIT_FLAG_OF).map((flag) => [flag, false])
);

/*
  Where the detail switches start when sharing is turned on.

  Description and files only. The other three each disclose something the
  customer is more likely to want to decide deliberately -- what the asset is
  worth, who inherits it, who advises on it -- so switching sharing on does not
  quietly answer those three for them. They are one tap away in the same
  section.

  Applied on every off -> on transition, not just the first, so an asset that
  was un-shared and shared again starts from the same place rather than
  silently restoring choices made before it was withdrawn.
*/
export const DEFAULT_ON_SHARING = {
  share_description: true,
  share_files: true,
  share_evaluations: false,
  share_beneficiaries: false,
  share_trusted_advisor: false,
  /*
    And nothing editable. Letting a partner read an asset has never been the
    same decision as letting them change it, so turning sharing on grants
    exactly what it used to and no more. Every edit is a separate tap.
  */
  ...EMPTY_EDIT_SHARING,
};

// Master off by default — an asset is never shared until the user says so.
export const EMPTY_PARTNER_SHARING = {
  share_with_partner: false,
  ...DEFAULT_ON_SHARING,
};

// The rows revealed by the master switch, in the design's order.
const DETAIL_ROWS = [
  { field: "share_description", labelKey: "partnerSharingText2" },
  { field: "share_files", labelKey: "partnerSharingText3" },
  { field: "share_evaluations", labelKey: "partnerSharingText4" },
  /*
    Above the advisor row, and the pair say the same kind of thing: who is
    attached to this asset. Both disclose a name and nothing else -- the
    person's own record stays behind their own switch.
  */
  { field: "share_beneficiaries", labelKey: "partnerSharingText18" },
  { field: "share_trusted_advisor", labelKey: "partnerSharingText5" },
];

const FALLBACK = {
  partnerSharingText1: "Share the asset with {name}",
  partnerSharingText2: "Share the asset's description",
  partnerSharingText3: "Share the asset's files",
  partnerSharingText4: "Share the asset's evaluations",
  partnerSharingText5: "Share the asset's trusted advisor",
  partnerSharingText18: "Share the asset's beneficiaries",
  partnerSharingText6: "Your credentials cannot be shared with {name}.",
  partnerSharingText36: "Shared with you",
  partnerSharingText37: "You may:",
  partnerSharingText7: "You can modify this at anytime.",
  partnerSharingText8: "No",
  partnerSharingText9: "Yes",
  partnerSharingText10:
    "Choose what your partner can see for this asset. Your credentials are never shared, and you can change this at any time.",
  partnerSharingText11: "{ partner name }",
};

/*
  Matches the switches in the Asset Evaluation section: 24x15 with a 12px
  thumb, measured off the design export.
*/
export const switchSx = {
  width: 24,
  height: 15,
  padding: 0,
  display: "flex",
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: "1.5px",
    transitionDuration: "200ms",
    "&.Mui-checked": {
      transform: "translateX(9px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#34C759",
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 12,
    height: 12,
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 15 / 2,
    backgroundColor: "#E5E5EA",
    opacity: 1,
  },
};

/*
  The user's partner, or null when they have none — which is what decides
  whether this section is rendered at all.

  Frontend only, and deliberately the ONE place that decides it. There is still
  no `partner` field on any response: the relationship is derived server-side
  from referral events and never handed to the client (see
  docs/partner-association.md). What IS handed over is `UserData.legal[0]` —
  built by Routes/Prefilled.js from the partner's own office details, carrying
  their name and logo. That is the only real partner data the frontend can see
  today, so it is what the section is keyed on.

  Two consequences, both deliberate:

  - A partner who is not an agency has no legal entry, so their customers do not
    see the section yet. Better than showing it with the "{ partner name }"
    placeholder where a name belongs.
  - `CustomerUser.register_code` would catch those users too, but it carries no
    name, and a supercode is not a partner's code — so it is not used.

  When the backend exposes a real partner object, replace the body of this
  function and nothing else changes.
*/
export const getPartner = (userData) => {
  const customer = userData?.CustomerUser;
  // A partner has no partner of their own — the same exclusion the
  // inheritance tree applies before it draws the legal node.
  const viewerType = String(customer?.user_type || "").toLowerCase();
  if (viewerType === "partner" || viewerType === "influencer") return null;

  /*
    The same flag the tree checks before drawing the legal node, and the reason
    this section now disappears when a partner is deleted.

    Deleting a partner in the admin tool is a soft delete, so the copy of their
    office details on this user's document outlives them. The server reports
    `show_legal_data` as false once the partner behind it is gone
    (Routes/User.js, POST /user/customer), which retires both surfaces at once.
    Keying on `legal[0]` alone is not enough: that entry is also where the demo
    "Notary Paris Co." card lands for users who prefill with sample data, and
    that one is nobody's partner.
  */
  /*
    The real thing, from POST /user/mypartner via DataContext -- resolved from
    the promo code the customer signed up with, and already null when the
    partner has been deleted or has no customer-management permission.

    This replaced a guess made from the legal-card snapshot, which was the best
    available before the endpoint existed: it could only ever surface a name,
    and it confused the demo "Notary Paris Co." card for a partner.
  */
  const partner = userData?.myPartner;
  const name = String(partner?.name || "").trim();
  if (!name) return null;

  return { name, avatar: partner.profile_avatar || null };
};

function PartnerSharing({
  value,
  onChange,
  disabled = false,
  /*
    The partner's display name and picture. Both are placeholders until the
    backend exposes the partner relationship — see the spec.
  */
  partnerName,
  partnerAvatar,
  /*
    Who is reading it. See the note on PartnerSharingPerson: for the partner
    these sentences are about them and addressed to somebody else -- "Share
    the asset with {them}", "Your credentials cannot be shared with {them}"
    -- so that side gets its own two labels and no note. The rows and the
    View/Edit headings are the same either way.
  */
  viewer = "customer",
}) {
  const asPartner = viewer === "partner";
  const websiteCurrentLang = useContext(LanguageContext).language;
  const [content, setContent] = useState(
    websiteCurrentLang?.content?.User?.EditAsset?.PartnerSharing || FALLBACK
  );
  useEffect(() => {
    setContent(
      websiteCurrentLang?.content?.User?.EditAsset?.PartnerSharing || FALLBACK
    );
  }, [websiteCurrentLang]);
  const t = (key) => (content && content[key]) || FALLBACK[key];

  const [internal, setInternal] = useState(value || EMPTY_PARTNER_SHARING);
  // Controlled when the parent passes `value`, uncontrolled otherwise.
  const sharing = value || internal;

  const update = (patch) => {
    /*
      Turning the master on resets the detail switches to DEFAULT_ON_SHARING
      rather than revealing whatever they happened to hold. Without it the rows
      appear carrying values from before the asset was un-shared -- or, on a
      record that predates a flag, the schema default -- and the customer is
      sharing more than the switch they just touched says.

      Only on the off -> on edge. Turning it off leaves the details alone, which
      is what the server already assumes: it ignores them whenever
      share_with_partner is false.
    */
    const turningOn =
      patch.share_with_partner === true && !sharing.share_with_partner;
    let next = turningOn
      ? { ...sharing, ...DEFAULT_ON_SHARING, ...patch }
      : { ...sharing, ...patch };

    /*
      Edit implies View, in both directions, whichever switch was touched.

      Turning Edit on turns View on: a partner who may rewrite the
      description can obviously read it, and leaving View off would promise a
      privacy the edit itself breaks. Turning View off turns Edit off: access
      withdrawn is access withdrawn, and an edit flag left on under a closed
      view flag is a permission waiting to come back the next time the
      customer re-shares the field.

      The server applies the same rule to every proposal, so this is the form
      agreeing with it rather than the form deciding it.
    */
    for (const [view, edit] of Object.entries(EDIT_FLAG_OF)) {
      if (patch[edit] === true) next = { ...next, [view]: true };
      if (patch[view] === false) next = { ...next, [edit]: false };
    }
    // Nothing is editable on an asset that is not shared at all.
    if (next.share_with_partner !== true) {
      next = { ...next, ...EMPTY_EDIT_SHARING };
    }
    if (!value) setInternal(next);
    if (onChange) onChange(next);
  };

  // Falls back to the design's literal "{ partner name }" placeholder.
  const name = String(partnerName || "").trim() || t("partnerSharingText11");
  const withName = (key) => t(key).replace("{name}", name);

  /*
    Bring the detail switches into view when the master is turned on.

    They are always present now, but on a modal that is already scrolled they
    sit below the fold — so without this the user turns sharing on and sees
    nothing happen. Five switches decide what a partner may read; they should
    not be something you have to go looking for.

    Still worth doing now that they are never unmounted: the click wakes them
    from grey to live, which is exactly the moment they become worth looking
    at, and `block: "nearest"` does nothing at all when they are already in
    view.

    Waited out rather than scrolled immediately: the modal is sized by its
    contents and animates to its new height over SMOOTH_HEIGHT_MS (see
    Utils/useSmoothHeight). Scrolling while it is still growing aims at a
    target that has not arrived yet and lands short.

    `block: "nearest"` so it scrolls the minimum needed — the rows come into
    view without yanking the asset's own fields off the top of the modal.
  */
  const detailRowsRef = useRef(null);
  /*
    Seeded with the value the section OPENS on, not with false.

    Seeded false, an asset that is already shared counted as having just been
    switched on the moment the modal appeared, and the modal scrolled itself
    down to these rows before the reader had looked at the asset. The scroll
    is for the person who just flipped the switch; opening a record is not
    flipping it.
  */
  const wasOnRef = useRef(Boolean(sharing.share_with_partner));

  useEffect(() => {
    const on = Boolean(sharing.share_with_partner);
    const justTurnedOn = on && !wasOnRef.current;
    wasOnRef.current = on;
    if (!justTurnedOn) return undefined;

    const reduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    )?.matches;
    const settle = setTimeout(
      () => {
        detailRowsRef.current?.scrollIntoView({
          behavior: reduced ? "auto" : "smooth",
          block: "nearest",
        });
      },
      reduced ? 0 : SMOOTH_HEIGHT_MS + 30
    );
    return () => clearTimeout(settle);
  }, [sharing.share_with_partner]);

  const isOn = Boolean(sharing.share_with_partner);

  const renderToggle = (field, label, rowDisabled = false) => (
    <div className={styles.toggle}>
      <span className={styles.toggle_side}>{t("partnerSharingText8")}</span>
      <Switch
        sx={switchSx}
        /*
          Drawn off while the row is inert, whatever the record holds. With the
          master off nothing is shared -- the server ignores every detail flag
          when share_with_partner is false -- so a switch sitting in the on
          position would be claiming something that is not true.

          Display only. The stored values are left alone: turning the master
          back on resets them to DEFAULT_ON_SHARING anyway, and rewriting them
          here would be a second place that decides what is shared.
        */
        checked={rowDisabled ? false : Boolean(sharing[field])}
        disabled={disabled || rowDisabled}
        onChange={(e) => update({ [field]: e.target.checked })}
        inputProps={{ "aria-label": label }}
      />
      <span className={styles.toggle_side}>{t("partnerSharingText9")}</span>
    </div>
  );

  return (
    <div className={styles.section}>
      <div className={styles.body}>
        <div className={styles.rows}>
          <div className={styles.row}>
            <div className={styles.title}>
              <span>
                {asPartner
                  ? t("partnerSharingText36")
                  : withName("partnerSharingText1")}
              </span>
              <Info message={t("partnerSharingText10")} />
            </div>
            {renderToggle(
              "share_with_partner",
              withName("partnerSharingText1")
            )}
          </div>

          {/*
            Always rendered, greyed and inert until the master is on -- the
            same treatment PartnerSharingPerson gives its contact row.

            Hidden, they made the master switch look like the whole of the
            choice: nothing on screen said that what a partner may read is
            itself a set of decisions until you had already agreed to share.
            Present and greyed, the choice is visible before it is taken.
          */}
          <div className={styles.detail_rows} ref={detailRowsRef}>
            {/*
              Two columns now, not one. The customer decides per field what
              the partner may READ and, separately, what they may CHANGE --
              and a field can be neither, which is what both switches off
              means.
            */}
            <div
              className={`${styles.row} ${styles.column_head} ${
                isOn ? "" : styles.row_disabled
              }`}
            >
              <div className={styles.detail_label}>
                {asPartner
                  ? t("partnerSharingText37")
                  : withName("partnerSharingText19")}
              </div>
              <div className={styles.column_label}>
                {t("partnerSharingText20")}
              </div>
              <div className={styles.column_label}>
                {t("partnerSharingText21")}
              </div>
            </div>
            {DETAIL_ROWS.map(({ field, labelKey }) => (
              <div
                className={`${styles.row} ${styles.detail_row} ${
                  isOn ? "" : styles.row_disabled
                }`}
                key={field}
              >
                <div className={styles.detail_label}>{t(labelKey)}</div>
                {renderToggle(field, t(labelKey), !isOn)}
                {/*
                  Live whether or not the field is shared yet. Somebody who
                  reaches for Edit has already decided the partner may touch
                  this field, and refusing them until they find the View
                  switch first makes them spell out a conclusion they have
                  plainly drawn -- so Edit turns View on with it (`update`),
                  and the row ends up in the only state that makes sense.

                  Inert only while the master is off, where nothing is shared
                  at all and there is no field to grant anything on.
                */}
                {renderToggle(
                  EDIT_FLAG_OF[field],
                  `${t(labelKey)} — ${t("partnerSharingText21")}`,
                  !isOn
                )}
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Addressed to the customer -- "Your credentials", "you approve". */}
      {isOn && !asPartner && (
        <div className={styles.note}>
          <div>{withName("partnerSharingText6")}</div>
          <div>{t("partnerSharingText7")}</div>
          {/*
            Said out loud, because "Edit" on a switch reads like the partner
            can change the value outright. They cannot: every change waits
            for this customer to accept it.
          */}
          <div>{withName("partnerSharingText22")}</div>
        </div>
      )}
    </div>
  );
}

export default PartnerSharing;
